// frontend/src/utils/bookingImportMapping.js
//
// Maps a CSV's headings onto the booking importer's field names.
//
// This exists because the admin console asked for "start time, end time or duration" while the file clearly
// had a Time and a Duration column: the guesser only matched exact header keys ("time" was not in the list,
// "Duration (min)" did not match "duration"), so the columns silently stayed unmapped. Guesswork that is
// wrong should be visibly wrong, and guesswork that is right should just work — hence a tolerant matcher
// plus an explicit list of what is still missing.

const BOOKING_FIELDS = {
  staffname: "staffName",
  staff: "staffName",
  staffmember: "staffName",
  employee: "staffName",
  professional: "staffName",
  customername: "customerName",
  customer: "customerName",
  client: "customerName",
  guest: "customerName",
  customeremail: "customerEmail",
  email: "customerEmail",
  emailaddress: "customerEmail",
  customerphone: "customerPhone",
  phone: "customerPhone",
  phonenumber: "customerPhone",
  mobile: "customerPhone",
  appointmentdate: "appointmentDate",
  date: "appointmentDate",
  bookingdate: "appointmentDate",
  starttime: "startTime",
  time: "startTime",
  start: "startTime",
  appointmenttime: "startTime",
  endtime: "endTime",
  end: "endTime",
  duration: "duration",
  durationmin: "duration",
  durationminutes: "duration",
  minutes: "duration",
  length: "duration",
  servicename: "serviceName",
  service: "serviceName",
  price: "price",
  amount: "price",
  notes: "notes",
  note: "notes",
  status: "status",
};

// Order matters: the more specific needles come before the looser ones, so "Customer Email" is not
// swallowed by the "customer" rule.
const CONTAINS = [
  ["customeremail", "customerEmail"],
  ["clientemail", "customerEmail"],
  ["customerphone", "customerPhone"],
  ["clientphone", "customerPhone"],
  ["servicename", "serviceName"],
  ["duration", "duration"],
  ["minutes", "duration"],
  ["starttime", "startTime"],
  ["endtime", "endTime"],
  ["appttime", "startTime"],
  ["start", "startTime"],
  // Last of the time rules on purpose: "start"/"end" are checked above, so a bare "time" or "appointment
  // time" only lands here and reads as the start of the appointment.
  ["time", "startTime"],
  ["customername", "customerName"],
  ["clientname", "customerName"],
  ["staffname", "staffName"],
  ["employee", "staffName"],
  ["date", "appointmentDate"],
  ["price", "price"],
  ["amount", "price"],
  ["note", "notes"],
  ["status", "status"],
];

const FIELD_LABELS = {
  customerName: "customer name",
  customerEmail: "customer email",
  appointmentDate: "date",
  startTime: "start time",
  endTime: "end time",
  duration: "duration",
};

const BOOKING_FIELD_OPTIONS = [
  { value: "customerName", label: "Customer name", required: true },
  { value: "customerEmail", label: "Customer email", required: true },
  { value: "customerPhone", label: "Customer phone" },
  { value: "appointmentDate", label: "Appointment date", required: true },
  { value: "startTime", label: "Start time", required: true },
  { value: "endTime", label: "End time (or use duration)" },
  { value: "duration", label: "Duration in minutes (or use end time)" },
  { value: "staffName", label: "Staff / professional name" },
  { value: "serviceName", label: "Service name" },
  { value: "price", label: "Price" },
  { value: "notes", label: "Notes" },
  { value: "status", label: "Status" },
];

const normalise = (header) => String(header).toLowerCase().replace(/[^a-z0-9]/g, "");

/** Best guess of our field name for one CSV heading, or "" when nothing fits. */
function guessBookingField(header) {
  const key = normalise(header);
  if (BOOKING_FIELDS[key]) return BOOKING_FIELDS[key];
  for (const [needle, field] of CONTAINS) {
    if (key.includes(needle)) return field;
  }
  return "";
}

/** A whole header row at once, as { csvHeader: fieldName }. */
function guessBookingMapping(headers) {
  const mapping = {};
  for (const header of headers || []) mapping[header] = guessBookingField(header);
  return mapping;
}

/**
 * Required fields still unmapped. "End time or duration" is satisfied by either, and is reported as
 * `endTimeOrDuration` so the UI can say that rather than demand both.
 */
function missingBookingFields(mapping) {
  const mapped = Object.values(mapping || {}).filter(Boolean);
  const missing = [];
  if (!mapped.includes("customerName")) missing.push("customerName");
  if (!mapped.includes("customerEmail")) missing.push("customerEmail");
  if (!mapped.includes("appointmentDate")) missing.push("appointmentDate");
  if (!mapped.includes("startTime")) missing.push("startTime");
  if (!mapped.includes("endTime") && !mapped.includes("duration")) {
    missing.push("endTimeOrDuration");
  }
  return missing;
}

function describeField(field) {
  return field === "endTimeOrDuration" ? "end time or duration" : FIELD_LABELS[field] || field;
}

module.exports = {
  BOOKING_FIELDS,
  BOOKING_FIELD_OPTIONS,
  FIELD_LABELS,
  guessBookingField,
  guessBookingMapping,
  missingBookingFields,
  describeField,
};
